import React, { useState, useCallback, useRef, useEffect } from 'react';

/**
 * InlineItemEditor
 *
 * Generic inline editable item row used across FoodMenu sections and Catering tables.
 *
 * Modes:
 *  1. "menu"     — fields: name, desc
 *  2. "catering" — fields: name, half, full (tray pricing)
 *  3. "table"    — fields: name + N value columns (generic table rows)
 *
 * Props:
 *  item      — { name, desc? }  OR  [name, ...cols]  depending on mode
 *  mode      — 'menu' | 'catering' | 'table'
 *  colLabels — labels for the value columns, e.g. ['Dosa','Pesarattu','Ragi Dosa'].
 *              Defaults to [col2Label, col3Label] so existing two-column callers
 *              (catering trays) keep working unchanged.
 *  col2Label — label for 2nd column (e.g. 'Half', 'Min.')
 *  col3Label — label for 3rd column (e.g. 'Full', 'Price')
 *  onSave    — called with updated item in same format as input
 *  onDelete  — called when delete is confirmed
 */
const InlineItemEditor = ({ item, mode = 'menu', col2Label = 'Half', col3Label = 'Full', colLabels, onSave, onDelete, autoEdit = false, onMoveUp, onMoveDown, canEditPrice = false }) => {
  const [editing, setEditing] = useState(autoEdit);

  // Value-column labels; two columns unless a caller asks for more.
  const labels = React.useMemo(
    () => (colLabels?.length ? colLabels : [col2Label, col3Label]),
    [colLabels, col2Label, col3Label],
  );

  /* ── Normalize item to { name, desc, col2, col3 } ── */
  const normalized = React.useMemo(() => {
    if (mode === 'menu') {
      if (typeof item === 'string') return { name: item, desc: '', price: '', cols: [] };
      return { name: item.name || '', desc: item.desc || '', price: item.price != null ? String(item.price) : '', cols: [] };
    }
    // catering / table — item is [name, ...cols]
    const arr = Array.isArray(item) ? item : [item];
    return {
      name: String(arr[0] ?? ''),
      desc: '',
      price: '',
      cols: labels.map((_, i) => (arr[i + 1] == null ? '' : String(arr[i + 1]))),
    };
  }, [item, mode, labels]);

  const [draft, setDraft] = useState(normalized);

  // Sync draft if item changes (e.g. after external save)
  useEffect(() => { setDraft(normalized); }, [normalized]);

  const nameRef = useRef(null);
  useEffect(() => { if (editing) nameRef.current?.focus(); }, [editing]);

  const handleEdit = () => {
    setDraft(normalized);
    setEditing(true);
  };

  const handleSave = useCallback(() => {
    if (!draft.name.trim()) return;
    if (mode === 'menu') {
      const priceVal = canEditPrice && draft.price !== '' ? parseFloat(draft.price) : undefined;
      // Start from the original item so fields this form doesn't show survive
      // the edit — e.g. a section item's `img`, or the per-base `pricing`
      // override on Pesarattu / Ragi Dosa. Rebuilding from scratch silently
      // dropped them, which reset those combos to the category default.
      const original = item && typeof item === 'object' && !Array.isArray(item) ? item : {};
      const obj = { ...original, name: draft.name.trim() };

      if (draft.desc?.trim()) obj.desc = draft.desc.trim();
      else delete obj.desc;

      if (priceVal != null && !isNaN(priceVal)) obj.price = Math.round(priceVal * 100) / 100;
      else if (canEditPrice) delete obj.price;   // cleared on purpose
      // else: not allowed to edit price — keep whatever `original` carried.

      onSave?.(obj);
    } else {
      onSave?.([draft.name.trim(), ...draft.cols.map((c) => c.trim())]);
    }
    setEditing(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, mode, item, canEditPrice, onSave]);

  const handleCancel = () => {
    setDraft(normalized);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${normalized.name}"?`)) {
      onDelete?.();
    }
  };

  if (editing) {
    return (
      <div className="ae-form">
        <input
          ref={nameRef}
          className="ae-input"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          onKeyDown={handleKeyDown}
          placeholder="Item name"
        />
        {mode === 'menu' && (
          <>
            <input
              className="ae-input ae-input--md"
              value={draft.desc}
              onChange={(e) => setDraft((d) => ({ ...d, desc: e.target.value }))}
              onKeyDown={handleKeyDown}
              placeholder="Description (optional)"
            />
            {canEditPrice && (
              <input
                className="ae-input ae-input--sm"
                type="number"
                min="0"
                step="0.01"
                value={draft.price}
                onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
                onKeyDown={handleKeyDown}
                placeholder="Price (e.g. 8.99)"
              />
            )}
          </>
        )}
        {(mode === 'catering' || mode === 'table') && labels.map((label, i) => (
          <input
            key={label || i}
            className="ae-input ae-input--sm"
            value={draft.cols[i] ?? ''}
            onChange={(e) => setDraft((d) => ({
              ...d, cols: d.cols.map((c, j) => (j === i ? e.target.value : c)),
            }))}
            onKeyDown={handleKeyDown}
            placeholder={label}
            aria-label={label}
          />
        ))}
        <div className="ae-form__btns">
          <button className="ae-btn-save" onClick={handleSave}>✓</button>
          <button className="ae-btn-cancel" onClick={handleCancel}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ae-row">
      <div className="ae-row__name">
        {normalized.name}
        {mode === 'menu' && normalized.price && (
          <span className="ae-row__price">${parseFloat(normalized.price).toFixed(2)}</span>
        )}
        {mode === 'menu' && normalized.desc && (
          <div className="ae-row__sub">{normalized.desc}</div>
        )}
      </div>
      {(mode === 'catering' || mode === 'table') && (
        <div className="ae-row__prices">
          {normalized.cols.map((c, i) => (
            <span className="ae-row__price-cell" key={labels[i] || i}>{c}</span>
          ))}
        </div>
      )}
      <div className="ae-row__btns">
        <button className="ae-icon-btn ae-icon-btn--move" onClick={onMoveUp}  disabled={!onMoveUp}  title="Move up">↑</button>
        <button className="ae-icon-btn ae-icon-btn--move" onClick={onMoveDown} disabled={!onMoveDown} title="Move down">↓</button>
        <button className="ae-icon-btn" onClick={handleEdit} title="Edit">✏️</button>
        <button className="ae-icon-btn ae-icon-btn--del" onClick={handleDelete} title="Delete">🗑️</button>
      </div>
    </div>
  );
};

export default InlineItemEditor;
